export const NADI_HOUSE_FIELDS = [
  { key: "goodHouses", label: "Good", color: "#2e7d32" },
  { key: "badHouses", label: "Bad", color: "#c62828" },
  { key: "goodSupportiveHouses", label: "Supportive Good", color: "#1565c0" },
  { key: "badSupportiveHouses", label: "Supportive Bad", color: "#6d4c41" },
] as const;
export type NadiHouseField = (typeof NADI_HOUSE_FIELDS)[number]["key"];
export type NadiCustomEvent = { id: string; label: string } & Record<
  NadiHouseField,
  number[]
>;
export type NadiEventDraft = { label: string } & Record<NadiHouseField, string>;
export const emptyNadiEventDraft = (): NadiEventDraft => ({
  label: "",
  goodHouses: "",
  badHouses: "",
  goodSupportiveHouses: "",
  badSupportiveHouses: "",
});
export const nadiEventKey = (id: string) => `custom:${id}`;
export function parseNadiHouses(value: string): number[] {
  const text = value.trim();
  if (!text) return [];
  if (!/^\d{1,2}(?:[\s,;]+\d{1,2})*[\s,;]*$/.test(text))
    throw new Error(
      "Use house numbers from 1 to 12, separated by commas or spaces.",
    );
  const values = text
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(Number);
  if (values.some((h) => h < 1 || h > 12))
    throw new Error("Use house numbers from 1 to 12.");
  return Array.from(new Set(values)).sort((a, b) => a - b);
}
export function buildNadiCustomEvent(
  draft: NadiEventDraft,
): Omit<NadiCustomEvent, "id"> {
  const label = draft.label.trim() || "Custom event";
  if (label.length > 80)
    throw new Error("Event name must contain at most 80 characters.");
  const event = { label } as Omit<NadiCustomEvent, "id">;
  for (const { key } of NADI_HOUSE_FIELDS)
    event[key] = parseNadiHouses(draft[key]);
  if (!NADI_HOUSE_FIELDS.some(({ key }) => event[key].length))
    throw new Error("Add at least one house to any of the four fields.");
  return event;
}
export function nadiEventDraft(event: NadiCustomEvent): NadiEventDraft {
  return {
    label: event.label,
    ...Object.fromEntries(
      NADI_HOUSE_FIELDS.map(({ key }) => [key, event[key].join(", ")]),
    ),
  } as NadiEventDraft;
}
export function mergeNadiEvents<T>(
  builtins: Record<string, T>,
  custom: NadiCustomEvent[],
): Record<string, T | NadiCustomEvent> {
  return {
    ...builtins,
    ...Object.fromEntries(
      custom.map((event) => [nadiEventKey(event.id), event]),
    ),
  };
}

export type NadiHouseGroups = Partial<
  Record<NadiHouseField, readonly number[]>
>;

// Keep the existing precedence when a house belongs to multiple groups.
export function getNadiHouseHighlight(
  event: NadiHouseGroups | undefined,
  house: number,
  enabled = true,
) {
  if (!enabled || !event) return undefined;
  return NADI_HOUSE_FIELDS.find(({ key }) => event[key]?.includes(house));
}
